import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, downloadFile, apiError } from "@/lib/api";
import {
  computeTotals, formatEUR, VAT_RATES, UNITS, STATUS_OPTIONS, TEMPLATES, todayISO, addDaysISO,
} from "@/lib/invoice";
import { cn } from "@/lib/utils";
import { InvoicePreview } from "@/components/InvoicePreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Building2, User, CalendarDays, ListChecks, FileText, Wallet, Plus, Trash2,
  Save, FileDown, Loader2, GripVertical, ArrowLeft, LayoutTemplate,
} from "lucide-react";
import { toast } from "sonner";

const inputCls = "rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-primary h-9";
const CUSTOM = "__custom__";
const NONE = "__none__";

const blankItem = () => ({ description: "", quantity: 1, unit: "Stk.", unit_price: 0, vat_rate: 19 });

const defaultInvoice = () => ({
  invoice_number: "",
  template: "modern",
  senderId: "",
  sender: {},
  recipientId: "",
  recipient: { name: "", company: "", address_line1: "", zip: "", city: "", country: "Deutschland", email: "", customer_number: "" },
  items: [blankItem()],
  invoice_date: todayISO(),
  service_date: "",
  service_date_end: "",
  due_date: addDaysISO(14),
  payment_terms: "date",
  is_kleinunternehmer: false,
  intro_text: "Vielen Dank für Ihren Auftrag. Hiermit stelle ich Ihnen folgende Leistungen in Rechnung:",
  outro_text: "Vielen Dank für die gute Zusammenarbeit.",
  payment_status: "offen",
  paid_amount: 0,
});

export default function InvoiceEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get("duplicate");
  const navigate = useNavigate();
  const [inv, setInv] = useState(defaultInvoice());
  const [senders, setSenders] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(!!id || !!duplicateId);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");

  const set = (patch) => setInv((prev) => ({ ...prev, ...patch }));
  const totals = useMemo(() => computeTotals(inv.items, inv.is_kleinunternehmer), [inv.items, inv.is_kleinunternehmer]);

  useEffect(() => {
    const boot = async () => {
      try {
        const [sRes, rRes, pRes] = await Promise.all([
          api.get("/senders"), api.get("/recipients"), api.get("/positions"),
        ]);
        setSenders(sRes.data);
        setRecipients(rRes.data);
        setPositions(pRes.data);

        if (id) {
          const res = await api.get(`/invoices/${id}`);
          const d = res.data;
          const matchS = sRes.data.find((x) => x.logo_path === d.sender?.logo_path && x.label === d.sender?.label);
          setInv({
            ...defaultInvoice(),
            ...d,
            senderId: matchS?.id || CUSTOM,
            recipientId: CUSTOM,
          });
        } else if (duplicateId) {
          const [res, num] = await Promise.all([
            api.get(`/invoices/${duplicateId}`),
            api.get("/invoices/next-number"),
          ]);
          const d = res.data;
          const matchS = sRes.data.find((x) => x.logo_path === d.sender?.logo_path && x.label === d.sender?.label);
          delete d.id;
          setInv({
            ...defaultInvoice(),
            ...d,
            invoice_number: num.data.invoice_number,
            invoice_date: todayISO(),
            payment_status: "offen",
            paid_amount: 0,
            senderId: matchS?.id || CUSTOM,
            recipientId: CUSTOM,
          });
          toast.success("Rechnung dupliziert – zum Anlegen auf „Speichern“ klicken");
        } else {
          const num = await api.get("/invoices/next-number");
          setInv((prev) => {
            const first = sRes.data[0];
            return {
              ...prev,
              invoice_number: num.data.invoice_number,
              senderId: first?.id || "",
              sender: first ? stripMeta(first) : {},
              is_kleinunternehmer: first?.is_kleinunternehmer || false,
            };
          });
        }
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [id, duplicateId]);

  const onSelectSender = (val) => {
    if (val === CUSTOM) { set({ senderId: CUSTOM }); return; }
    const s = senders.find((x) => x.id === val);
    if (s) set({ senderId: val, sender: stripMeta(s), is_kleinunternehmer: s.is_kleinunternehmer });
  };

  const onSelectRecipient = (val) => {
    if (val === NONE) return;
    if (val === CUSTOM) { set({ recipientId: CUSTOM }); return; }
    const r = recipients.find((x) => x.id === val);
    if (r) {
      const { id: _i, user_id, created_at, ...rest } = r;
      set({ recipientId: val, recipient: { ...defaultInvoice().recipient, ...rest } });
    }
  };

  const setRecipientField = (k, v) => setInv((p) => ({ ...p, recipientId: CUSTOM, recipient: { ...p.recipient, [k]: v } }));

  const updateItem = (i, patch) =>
    setInv((p) => ({ ...p, items: p.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const removeItem = (i) => setInv((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const addItem = () => setInv((p) => ({ ...p, items: [...p.items, blankItem()] }));
  const addFromTemplate = (val) => {
    const t = positions.find((x) => x.id === val);
    if (t) setInv((p) => ({ ...p, items: [...p.items, { description: t.description, quantity: 1, unit: t.unit, unit_price: t.unit_price, vat_rate: t.vat_rate }] }));
  };

  const buildPayload = () => ({
    invoice_number: inv.invoice_number,
    sender: inv.sender || {},
    recipient: inv.recipient || {},
    items: (inv.items || []).map((it) => ({
      description: it.description || "",
      quantity: Number(it.quantity) || 0,
      unit: it.unit || "",
      unit_price: Number(it.unit_price) || 0,
      vat_rate: Number(it.vat_rate) || 0,
    })),
    invoice_date: inv.invoice_date,
    service_date: inv.service_date || "",
    service_date_end: inv.service_date_end || "",
    due_date: inv.due_date || "",
    payment_terms: inv.payment_terms || "date",
    is_kleinunternehmer: !!inv.is_kleinunternehmer,
    intro_text: inv.intro_text || "",
    outro_text: inv.outro_text || "",
    payment_status: inv.payment_status || "offen",
    paid_amount: Number(inv.paid_amount) || 0,
    template: inv.template || "modern",
  });

  const save = async () => {
    if (!inv.sender?.name && !inv.sender?.company) { toast.error("Bitte einen Briefkopf auswählen."); return null; }
    if (!inv.recipient?.name && !inv.recipient?.company) { toast.error("Bitte einen Empfänger angeben."); return null; }
    setSaving(true);
    try {
      const payload = buildPayload();
      let saved;
      if (id) {
        saved = await api.put(`/invoices/${id}`, payload);
      } else {
        saved = await api.post("/invoices", payload);
        navigate(`/editor/${saved.data.id}`, { replace: true });
      }
      toast.success("Rechnung gespeichert");
      return saved.data;
    } catch (e) {
      toast.error(apiError(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const exportDoc = async (type) => {
    setExporting(type);
    try {
      let invoiceId = id;
      if (!invoiceId) {
        const saved = await save();
        if (!saved) { setExporting(""); return; }
        invoiceId = saved.id;
      } else {
        const s = await save();
        if (!s) { setExporting(""); return; }
      }
      await downloadFile(`/invoices/${invoiceId}/${type}`, `Rechnung_${inv.invoice_number}.${type}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting("");
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] grid grid-cols-1 md:grid-cols-12">
      {/* LEFT: definitions */}
      <div className="col-span-12 md:col-span-5 lg:col-span-4 h-full overflow-y-auto thin-scroll bg-white border-r border-slate-200 flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 sticky top-0 bg-white z-10">
          <button data-testid="back-btn" onClick={() => navigate("/rechnungen")} className="text-slate-400 hover:text-slate-900">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Definition</div>
            <div className="font-heading font-bold text-lg leading-none">
              {id ? "Rechnung bearbeiten" : duplicateId ? "Neue Rechnung (Duplikat)" : "Neue Rechnung"}
            </div>
          </div>
        </div>

        <div className="flex-1">
          <Accordion type="multiple" defaultValue={["template", "sender", "recipient", "details", "items"]} className="w-full">
            {/* TEMPLATE */}
            <Section value="template" icon={LayoutTemplate} title="Vorlage / Design">
              <div className="grid grid-cols-2 gap-2">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.value}
                    data-testid={`template-${t.value}`}
                    onClick={() => set({ template: t.value })}
                    className={cn(
                      "border p-2 text-left transition-colors duration-150",
                      inv.template === t.value
                        ? "border-primary ring-1 ring-primary bg-blue-50/40"
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <TemplateThumb variant={t.value} />
                    <div className="text-xs font-semibold mt-1.5 text-slate-800">{t.label}</div>
                    <div className="text-[10px] text-slate-400 leading-tight">{t.desc}</div>
                  </button>
                ))}
              </div>
            </Section>

            {/* SENDER */}
            <Section value="sender" icon={Building2} title="Briefkopf / Absender">
              {senders.length === 0 ? (
                <EmptyHint onClick={() => navigate("/bausteine")} label="Noch kein Briefkopf. Jetzt anlegen →" />
              ) : (
                <Field label="Briefkopf wählen">
                  <Select value={inv.senderId} onValueChange={onSelectSender}>
                    <SelectTrigger data-testid="select-sender" className={inputCls}><SelectValue placeholder="Briefkopf…" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {senders.map((s) => <SelectItem key={s.id} value={s.id}>{s.label} — {s.company || s.name}</SelectItem>)}
                      {inv.senderId === CUSTOM && <SelectItem value={CUSTOM}>Individuell (aus Rechnung)</SelectItem>}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              {(inv.sender?.name || inv.sender?.company) && (
                <div className="text-xs text-slate-500 border border-slate-100 bg-slate-50 p-2.5 mt-2 leading-relaxed">
                  <div className="font-medium text-slate-700">{inv.sender.company || inv.sender.name}</div>
                  {inv.sender.address_line1 && <div>{inv.sender.address_line1}</div>}
                  <div>{[inv.sender.zip, inv.sender.city].filter(Boolean).join(" ")}</div>
                </div>
              )}
            </Section>

            {/* RECIPIENT */}
            <Section value="recipient" icon={User} title="Empfänger">
              <Field label="Gespeicherten Empfänger wählen">
                <Select value={recipients.some((r) => r.id === inv.recipientId) ? inv.recipientId : ""} onValueChange={onSelectRecipient}>
                  <SelectTrigger data-testid="select-recipient" className={inputCls}><SelectValue placeholder="Auswählen oder unten eingeben…" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {recipients.length === 0 && <SelectItem value={NONE} disabled>Keine gespeichert</SelectItem>}
                    {recipients.map((r) => <SelectItem key={r.id} value={r.id}>{r.company || r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Field label="Firma"><Input data-testid="recipient-company" value={inv.recipient.company} onChange={(e) => setRecipientField("company", e.target.value)} className={inputCls} /></Field>
                <Field label="Name"><Input data-testid="recipient-name" value={inv.recipient.name} onChange={(e) => setRecipientField("name", e.target.value)} className={inputCls} /></Field>
              </div>
              <Field label="Straße & Nr."><Input value={inv.recipient.address_line1} onChange={(e) => setRecipientField("address_line1", e.target.value)} className={inputCls} /></Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="PLZ"><Input value={inv.recipient.zip} onChange={(e) => setRecipientField("zip", e.target.value)} className={inputCls} /></Field>
                <Field label="Ort" className="col-span-2"><Input value={inv.recipient.city} onChange={(e) => setRecipientField("city", e.target.value)} className={inputCls} /></Field>
              </div>
              <Field label="Kundennummer"><Input value={inv.recipient.customer_number} onChange={(e) => setRecipientField("customer_number", e.target.value)} className={inputCls} /></Field>
            </Section>

            {/* DETAILS */}
            <Section value="details" icon={CalendarDays} title="Rechnungsdetails">
              <Field label="Rechnungsnummer">
                <div className="flex gap-2">
                  <Input data-testid="invoice-number" value={inv.invoice_number} onChange={(e) => set({ invoice_number: e.target.value })} className={inputCls} />
                  <Button type="button" variant="outline" className="rounded-none h-9 shrink-0 text-xs"
                    onClick={async () => { const n = await api.get("/invoices/next-number"); set({ invoice_number: n.data.invoice_number }); }}>
                    Auto
                  </Button>
                </div>
              </Field>
              <Field label="Rechnungsdatum"><Input type="date" data-testid="invoice-date" value={inv.invoice_date} onChange={(e) => set({ invoice_date: e.target.value })} className={inputCls} /></Field>
              <Field label="Leistungszeitraum (von – bis)">
                <div className="grid grid-cols-2 gap-2">
                  <Input type="date" data-testid="service-date-start" value={inv.service_date} onChange={(e) => set({ service_date: e.target.value })} className={inputCls} />
                  <Input type="date" data-testid="service-date-end" value={inv.service_date_end} onChange={(e) => set({ service_date_end: e.target.value })} className={inputCls} />
                </div>
              </Field>
              <Field label="Zahlungskondition">
                <Select value={inv.payment_terms} onValueChange={(v) => set(v === "prompt" ? { payment_terms: v, due_date: "" } : { payment_terms: v })}>
                  <SelectTrigger data-testid="payment-terms" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="date">Zahlbar bis Fälligkeitsdatum</SelectItem>
                    <SelectItem value="prompt">Prompt und ohne Abzug</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {inv.payment_terms !== "prompt" && (
                <Field label="Fällig am">
                  <div className="flex gap-2">
                    <Input type="date" data-testid="due-date" value={inv.due_date} onChange={(e) => set({ due_date: e.target.value })} className={inputCls} />
                    {inv.due_date && (
                      <Button type="button" variant="outline" data-testid="clear-due-date" onClick={() => set({ due_date: "" })} className="rounded-none h-9 shrink-0 text-xs">
                        Entfernen
                      </Button>
                    )}
                  </div>
                </Field>
              )}
              <div className="flex items-center justify-between border border-slate-200 p-2.5 mt-1">
                <div>
                  <div className="text-sm font-medium">Kleinunternehmer (§19)</div>
                  <div className="text-[11px] text-slate-400">Keine MwSt. ausweisen</div>
                </div>
                <Switch data-testid="klein-switch" checked={inv.is_kleinunternehmer} onCheckedChange={(v) => set({ is_kleinunternehmer: v })} />
              </div>
            </Section>

            {/* ITEMS */}
            <Section value="items" icon={ListChecks} title={`Positionen (${inv.items.length})`}>
              {positions.length > 0 && (
                <Field label="Aus Baustein hinzufügen">
                  <Select value="" onValueChange={addFromTemplate}>
                    <SelectTrigger data-testid="select-position-template" className={inputCls}><SelectValue placeholder="Vorlage wählen…" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.description} ({formatEUR(p.unit_price)})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              <div className="space-y-2 mt-2">
                {inv.items.map((it, i) => (
                  <div key={i} data-testid={`item-${i}`} className="border border-slate-200 p-2.5 bg-white">
                    <div className="flex items-center gap-1 mb-2">
                      <GripVertical className="h-4 w-4 text-slate-300" />
                      <span className="text-[11px] font-mono text-slate-400">Pos. {i + 1}</span>
                      <button data-testid={`remove-item-${i}`} onClick={() => removeItem(i)} className="ml-auto text-slate-300 hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <Textarea
                      data-testid={`item-desc-${i}`}
                      value={it.description}
                      onChange={(e) => updateItem(i, { description: e.target.value })}
                      placeholder="Beschreibung der Leistung…"
                      rows={2}
                      className="rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-primary text-sm resize-none mb-2"
                    />
                    <div className="grid grid-cols-12 gap-1.5 items-end">
                      <div className="col-span-3">
                        <Label className="text-[10px] text-slate-400">Menge</Label>
                        <Input type="number" data-testid={`item-qty-${i}`} value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} className={`${inputCls} font-mono px-1.5`} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] text-slate-400">Einh.</Label>
                        <Select value={it.unit} onValueChange={(v) => updateItem(i, { unit: v })}>
                          <SelectTrigger className="rounded-none border-slate-300 h-9 px-1.5 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-none">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4">
                        <Label className="text-[10px] text-slate-400">Einzelpreis</Label>
                        <Input type="number" data-testid={`item-price-${i}`} value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })} className={`${inputCls} font-mono px-1.5`} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] text-slate-400">MwSt</Label>
                        <Select value={String(it.vat_rate)} onValueChange={(v) => updateItem(i, { vat_rate: Number(v) })} disabled={inv.is_kleinunternehmer}>
                          <SelectTrigger className="rounded-none border-slate-300 h-9 px-1 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-none">{VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="text-right text-xs font-mono text-slate-500 mt-1.5">
                      = {formatEUR((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}
                    </div>
                  </div>
                ))}
              </div>

              <Button type="button" variant="outline" data-testid="add-item" onClick={addItem} className="w-full rounded-none mt-2 gap-2 border-dashed">
                <Plus className="h-4 w-4" /> Position hinzufügen
              </Button>

              <div className="mt-3 border-t border-slate-200 pt-2 space-y-1 text-sm">
                <div className="flex justify-between text-slate-500"><span>Netto</span><span className="font-mono">{formatEUR(totals.net)}</span></div>
                {!inv.is_kleinunternehmer && <div className="flex justify-between text-slate-500"><span>MwSt.</span><span className="font-mono">{formatEUR(totals.vat)}</span></div>}
                <div className="flex justify-between font-semibold text-slate-900"><span>Gesamt</span><span className="font-mono">{formatEUR(totals.gross)}</span></div>
              </div>
            </Section>

            {/* TEXTS */}
            <Section value="texts" icon={FileText} title="Texte">
              <Field label="Einleitungstext">
                <Textarea value={inv.intro_text} onChange={(e) => set({ intro_text: e.target.value })} rows={2} className="rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-primary text-sm resize-none" />
              </Field>
              <Field label="Schlusstext">
                <Textarea value={inv.outro_text} onChange={(e) => set({ outro_text: e.target.value })} rows={2} className="rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-primary text-sm resize-none" />
              </Field>
            </Section>

            {/* PAYMENT */}
            <Section value="payment" icon={Wallet} title="Zahlung">
              <Field label="Status">
                <Select value={inv.payment_status} onValueChange={(v) => set({ payment_status: v })}>
                  <SelectTrigger data-testid="editor-status" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {inv.payment_status === "teilbezahlt" && (
                <Field label="Bereits gezahlt (€)">
                  <Input type="number" value={inv.paid_amount} onChange={(e) => set({ paid_amount: e.target.value })} className={`${inputCls} font-mono`} />
                </Field>
              )}
            </Section>
          </Accordion>
        </div>

        {/* Action bar */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-3 flex gap-2">
          <Button data-testid="save-invoice" onClick={save} disabled={saving} className="flex-1 rounded-none bg-primary hover:bg-[#00227A] gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
          </Button>
          <Button data-testid="export-pdf" onClick={() => exportDoc("pdf")} disabled={!!exporting} variant="outline" className="rounded-none gap-1.5">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
          </Button>
          <Button data-testid="export-docx" onClick={() => exportDoc("docx")} disabled={!!exporting} variant="outline" className="rounded-none gap-1.5">
            {exporting === "docx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Word
          </Button>
        </div>
      </div>

      {/* RIGHT: live preview */}
      <div className="col-span-12 md:col-span-7 lg:col-span-8 h-full overflow-y-auto thin-scroll bg-slate-100 p-6 lg:p-10 flex justify-center items-start">
        <InvoicePreview invoice={inv} />
      </div>
    </div>
  );
}

function stripMeta(s) {
  const { id, user_id, created_at, label, ...rest } = s;
  return { ...rest, label };
}

const Section = ({ value, icon: Icon, title, children }) => (
  <AccordionItem value={value} className="border-b border-slate-200">
    <AccordionTrigger className="px-5 py-3 hover:no-underline hover:bg-slate-50 text-sm font-semibold">
      <span className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </span>
    </AccordionTrigger>
    <AccordionContent className="px-5 pb-4 space-y-2.5">{children}</AccordionContent>
  </AccordionItem>
);

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className="text-xs text-slate-500">{label}</Label>
    {children}
  </div>
);

const EmptyHint = ({ onClick, label }) => (
  <button onClick={onClick} className="w-full text-left text-sm text-primary border border-dashed border-blue-300 bg-blue-50/50 p-3 hover:bg-blue-50">
    {label}
  </button>
);

const TemplateThumb = ({ variant }) => (
  <div className="aspect-[0.72] w-full bg-white border border-slate-200 p-1.5 overflow-hidden flex flex-col">
    {variant === "modern" ? (
      <>
        <div className="h-1.5 w-8 bg-blue-200 ml-auto mb-1" />
        <div className="h-2.5 w-14 bg-primary mb-1.5" />
        <div className="h-1.5 w-full bg-primary mb-1" />
        {[0, 1, 2].map((i) => <div key={i} className="h-1 w-full bg-slate-100 mb-1" />)}
        <div className="h-1.5 w-12 bg-slate-300 ml-auto mt-auto" />
      </>
    ) : (
      <>
        <div className="flex justify-between mb-1.5">
          <div className="h-2 w-7 bg-slate-800" />
          <div className="h-1 w-7 bg-slate-200" />
        </div>
        <div className="h-1 w-9 bg-slate-200 mb-1.5" />
        <div className="h-1.5 w-12 bg-slate-800 mx-auto mb-1.5" />
        <div className="h-px w-full bg-slate-800 mb-1" />
        {[0, 1, 2].map((i) => <div key={i} className="h-1 w-full bg-slate-100 mb-1" />)}
        <div className="h-px w-full bg-slate-800 mt-auto mb-0.5" />
      </>
    )}
  </div>
);
